// Core lib dependencies.
import moment 				from 'moment';

// Selectors.
import Selectors 			from '../../selectors.js';

// Config.
import Config				from '../../config/config.js';
import Enums				from '../../config/enums.js';

// Misc dependencices.
import GoalUtil 			from '../../utils/goal.js';
import MilestoneUtil		from '../../utils/milestone.js';

/**
 * Progress processing business logic.
 *
 * @namespace
 *
 * @memberof module:Goal.Model.Logic
 */
const ProgressProcessing = {

	/**
	 * Proccess local Goal type progress.
	 */
	localProgress: function(id, metricId, dates, goalType, previousProgress) {

		let _progress;

		// - - - - - - - - - - - - - - - - - -
		// Behave according to Goal type.
		switch(goalType) {

			// - - - - - - - - - - - - - - - - - -
			// Just has an end date target (see how many participants we can get before the end date).
			case Enums.GOAL_TYPE_DATE:

				// Dispatch action to update progress.
				_progress = ProgressProcessing.buildDateProgress(dates, null, previousProgress);

			break;

			// - - - - - - - - - - - - - - - - - -
			// Number target (no end date, can take as long as required to reach goal).
			case Enums.GOAL_TYPE_NUMBER:
			break;

			// - - - - - - - - - - - - - - - - - -
			// A number and a date target (see if we can reach the target before the end date).
			case Enums.GOAL_TYPE_DATENUMBER:
			break;
		}

		return _progress;
	},

	/**
	 * Builds a progress object for a Date response.
	 */
	buildDateProgress: function(dates, responseData, previousProgress) {

		// Define progress object.
		let _progress = ProgressProcessing.buildProgressObject();

		// Calculate percent locally based on time window progression.
		let _percent = 0;

		let _startTimestamp = moment(dates.start).valueOf();
		let _endTimestamp = moment(dates.end).valueOf();
		let _nowTimestamp = moment().valueOf();

		if(_nowTimestamp < _startTimestamp) {
			_percent = 0;
		} else if (_nowTimestamp >= _endTimestamp) {
			_percent = 100;
		} else {
			let _range    = _endTimestamp - _startTimestamp;
			let _nowDelta = _nowTimestamp - _startTimestamp;
			_percent 	  = ((_nowDelta/_range) * 10000) / 100;
			_percent = parseFloat(_percent.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0]);
		}

		_progress.percent = _percent;

		// If up to date response data exists use that.
		if(responseData) {

			_progress.value = responseData.value;

			// Calculate the participation rate.
			_progress.stats = ProgressProcessing.calculateParticipationRate(_progress, previousProgress);

		// Otherwise use previous values.
		} else {
			_progress.value = previousProgress.value;
			_progress.stats = previousProgress.stats;
		}

		return _progress;
	},

	/**
	 * Builds a progress object for a Number response.
	 */
	buildNumberProgress: function(milestones, responseData, previousProgress, targets) {

		// Define progress object.
		let _progress = ProgressProcessing.buildProgressObject();

		// Define initial values based on responseData, or attempt to calculate if not supplied.
		let _goal 	 = (responseData.goal) ? responseData.goal : targets.numeric;
		let _value 	 = (responseData.value) ? responseData.value : Math.round((_goal * (responseData.percent/100)));
		let _percent = (responseData.percent) ? responseData.percent : 0;

		// If we have some milestones.
		if(milestones) {

			_progress.milestones = {};

			// Loop over milestones.
			for(let milestoneKey in milestones) {

				milestoneKey = parseInt(milestoneKey);

				// The current milestone.
				let milestoneDef = milestones[milestoneKey];

				// The previous/next milestones.
				let previousMilestoneDef = (milestoneKey > 0) ? milestones[milestoneKey-1] : null;
				let nextMilestoneDef = ((milestoneKey+1) < (milestones.length)) ? milestones[milestoneKey+1] : null;

				// The calculated values.
				let newValue = 0;
				let newPercent = 0;

				// Calculate individual milestone percentages based on overall goal percentage returned.
				let milstoneProgress = ProgressProcessing.calculateMilestoneProgressForGoalPercentage(milestoneDef, previousMilestoneDef, nextMilestoneDef, _value);
				newValue 			= milstoneProgress.newValue;
				newPercent 			= milstoneProgress.newPercent;

				// Set new values.
				_progress.milestones[milestoneDef.id] = {
					value:newValue,
					percent:newPercent
				}
			}
		}

		_progress.value = _value;
		_progress.percent = _percent;

		// Calculate the participation rate.
		_progress.stats = ProgressProcessing.calculateParticipationRate(_progress, previousProgress);

		return _progress;
	},

	/**
	 * Builds a progress object for a DateNumber response.
	 */
	buildDateNumberProgress: function(milestones, responseData, previousProgress, targets) {

		// Define progress object.
		let _progress = ProgressProcessing.buildProgressObject();

		// Define initial values based on responseData, or attempt to calculate if not supplied.
		let _goal = (responseData.goal) ? responseData.goal : targets.numeric;
		let _percent = (responseData.percent) ? responseData.percent : 0;
		let _value 	 = (responseData.value) ? responseData.value : Math.round((_goal * (_percent/100)));

		// Keep track of values as we loop.
		let _totalValue = 0;
		let _totalPercent = 0;

		// If we have some milestones.
		if(milestones) {

			_progress.milestones = {};

			// Loop over milestones
			for(let milestoneKey in milestones) {

				milestoneKey = parseInt(milestoneKey);

				// The current milestone def.
				let milestoneDef = milestones[milestoneKey];

				// The previous/next milestone.
				let previousMilestoneDef = (milestoneKey > 0) ? milestones[milestoneKey-1] : null;
				let nextMilestoneDef = ((milestoneKey+1) < (milestones.length)) ? milestones[milestoneKey+1] : null;

				// The calculated values.
				let newValue = 0;
				let newPercent = 0;

				// If a milestone object exists in the reponse data matching the milestone def.
				if(responseData.milestones && responseData.milestones.hasOwnProperty(milestoneKey)) {

					// The current milestone.
					let milestone = responseData.milestones[milestoneKey];

					// Get the new percentage value.
					newPercent = milestone.percent;

					// Get the new value (if sent).
					if(milestone.value) {

						newValue = milestone.value;

					// Otherwise calculate it.
					} else {

						let milestoneGoal = milestoneDef.goal;
						if(previousMilestoneDef) {
							milestoneGoal = milestoneGoal - previousMilestoneDef.goal;
						}

						newValue = Math.round((milestoneGoal/100)*newPercent);
					}

				// Otherwise.
				} else {

					// Calculate individual milestone percentages based on overall goal percentage returned.
					let milstoneProgress = ProgressProcessing.calculateMilestoneProgressForGoalPercentage(milestoneDef, previousMilestoneDef, nextMilestoneDef, _value);
					newValue 			= milstoneProgress.newValue;
					newPercent 			= milstoneProgress.newPercent;
				}

				_totalValue += newValue;
				_totalPercent += newPercent;

				// Set new values.
				_progress.milestones[milestoneDef.id] = {
					value:newValue,
					percent:newPercent
				}
			}

			// Calculate overall percent and value based on aggregate milesone totals, if not already set.
			if(!_percent) {
				_percent = Math.ceil((_totalPercent / (milestones.length*100))*100);
			}
			if(!_value) {
				_value = (_totalValue) ? _totalValue : Math.round((_goal * (_percent/100)));
			}
		}

		_progress.value = _value;
		_progress.percent = _percent;

		// Calculate the participation rate.
		_progress.stats = ProgressProcessing.calculateParticipationRate(_progress, previousProgress);

		return _progress;
	},

	/**
	 * Calculate progress for an individual milestone based on overall percentage for the goal.
	 */
	calculateMilestoneProgressForGoalPercentage: function(milestoneDef, previousMilestoneDef, nextMilestoneDef, currentGoalValue) {

		// The current milestone goal.
		let milestoneGoal = milestoneDef.goal;

		// The previous milestone goal.
		let previousMilestoneGoal = (previousMilestoneDef) ? previousMilestoneDef.goal : 0;

		// Keep a running tally of milestone goal boundaries to this point.
		let milestoneLowerBoundary 	= previousMilestoneGoal;
		let milestoneUpperBoundary 	= milestoneGoal;

		let newPercent = 0;
		let newValue   = 0;

		let calculateValue = function(currentGoalValue, milestoneLowerBoundary) {

			return currentGoalValue - milestoneLowerBoundary;
		}

		let calculatePercent = function(milestoneProgressValue, milestoneGoal, previousMilestoneGoal) {

			let percent = ((milestoneProgressValue / (milestoneGoal - previousMilestoneGoal)) * 10000)/100;
			percent = parseFloat(percent.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0]);

			return percent;
		}

		// Work out the percentage of this milestone that is complete.
		if(currentGoalValue >= milestoneUpperBoundary) {

			newPercent = 100;
			newValue   = milestoneGoal;

			// Calculate overflow.
			if(!nextMilestoneDef) {

				let milestoneProgressValue 	= calculateValue(currentGoalValue, milestoneLowerBoundary);
				newPercent 					= calculatePercent(milestoneProgressValue, milestoneGoal, previousMilestoneGoal);
				newValue   					= milestoneProgressValue;
			}

		} else if ((currentGoalValue >= milestoneLowerBoundary) && (currentGoalValue < milestoneUpperBoundary)) {

			let milestoneProgressValue 	= calculateValue(currentGoalValue, milestoneLowerBoundary);
			newPercent 					= calculatePercent(milestoneProgressValue, milestoneGoal, previousMilestoneGoal);
			newValue   					= milestoneProgressValue;

		} else {

			newPercent = 0;
			newValue   = 0;
		}

		return {newPercent:newPercent, newValue:newValue};
	},

	/**
	 * Calculates participation rate based on previous/current progress values.
	 */
	calculateParticipationRate: function(progress, previousProgress) {

		// Get the current & previous rate stats object.
		let stats 			= progress.stats;
		let previousStats 	= previousProgress.stats;

		// Get the current timestamp.
		let now = moment().valueOf();

		// If there is a previous sample, generate statistics.
		if(previousStats.lastSampled) {

			// The amount of time that has passed since we last calculated.
			let msSinceLastSample 	= now - previousStats.lastSampled;
			let timespan 			= moment.duration(msSinceLastSample);

			// The number of participations since we last calculated.
			let valueIncrease 		= progress.value - previousStats.lastValue;

			// Work out how many participations per second we have had.
			let ratePerSecond		= valueIncrease / timespan.asSeconds();

			// Extrapolate figure into minutes.
			let ratePerMinute 		= ratePerSecond*60;

			// - - - - - - - - - - - - - - - - - -
			// History array so we can take an average over N minutes.
			let history = [];

			// Trim entries from history over N minutes old.
			let cutOffTimestamp = now - Config.statistics.maxHistoryAge;
			for(let historyKey in previousStats.rate.history) {

				let historyEntry 	 = previousStats.rate.history[historyKey];
				let sampledTimestamp = historyEntry.sampled;

				if(sampledTimestamp >= cutOffTimestamp) {
					history.push(historyEntry);
				}
			}

			// - - - - - - - - - - - - - - - - - -
			// Push latest stats into history array
			history.push({
				sampled:now,
				ratePerSecond:ratePerSecond
			});

			// - - - - - - - - - - - - - - - - - -
			// Calculate averages.
			let averageRatePerSecond = 0;
			for(let historyKey in history) {

				let historyEntry 	  = history[historyKey];
				averageRatePerSecond += historyEntry.ratePerSecond;
			}
			averageRatePerSecond 	 = averageRatePerSecond/history.length;

			let averageRatePerMinute = averageRatePerSecond*60;

			// - - - - - - - - - - - - - - - - - -
			// Store history.
			stats.rate.history 			 = history;

			// Store increase since last sample.
			stats.valueIncrease 		 = valueIncrease;

			// Store current.
			stats.rate.current.perSecond = ratePerSecond;
			stats.rate.current.perMinute = ratePerMinute;

			// Store averages.
			stats.rate.average.perSecond = averageRatePerSecond;
			stats.rate.average.perMinute = averageRatePerMinute;
		}

		// Update the last sampled time.
		stats.lastSampled = now;

		// Update the value.
		stats.lastValue = progress.value;

		return stats;
	},

	/**
	 * Calculates the dynamic interval for progress checking, based on participation rates.
	 */
	calculateDynamicInterval: function(previousProgressInterval, previousProgress) {

		// Default interval.
		let interval = previousProgressInterval;

		// Get the previous rate stats object.
		let previousStats = previousProgress.stats;

		// If we have some average stats calculated.
		if(previousStats.rate.average.hasOwnProperty('perSecond')) {

			// Adjust the interval by N amount.
			let delta = Config.intervals.decay;

			// If the current participation rate is > then the average.
			if(previousStats.rate.current.perSecond > previousStats.rate.average.perSecond) {

				// Reduce interval.
				interval -= delta;

			// Otherwise if it is lower.
			} else if (previousStats.rate.current.perSecond < previousStats.rate.average.perSecond) {

				// Increase interval.
				interval += delta;

			// If the participation rate is zero.
			} else if (previousStats.rate.current.perSecond === 0 && previousStats.rate.average.perSecond === 0) {

				// Increase interval.
				interval += delta;

			// Otherwise if it > 0 but is equal.
			} else {

				// Stay the same.
			}

			// Check interval is within lower and upper boundaries.
			if(interval < Config.intervals.progressMin) {
				interval = Config.intervals.progressMin;
			}
			if(interval > Config.intervals.progressMax) {
				interval = Config.intervals.progressMax;
			}
		}

		return interval;
	},

	/**
	 * Returns the dynamic interval for progress checking, based on participation rates.
	 */
	getDynamicInterval: function(getState, id) {

		// Default to config definition.
		let interval = Config.intervals.progress

		// Check if there is a dyanmically calculated override value in the state.
		let intervals = Selectors.getIntervals(getState(), id);
		if(intervals.progress) {
			interval = intervals.progress;
		}

		return interval;
	},

	/**
	 * Builds a template progress object.
	 */
	buildProgressObject: function() {

		return {
			value:0,
			percent:0,
			milestones:null,
			stats: {
				lastSampled:null,
				lastValue:null,
				valueIncrease:null,
				rate:{
					current:{
						perSecond:null,
						perMinute:null
					},
					average:{
						perSecond:null,
						perMinute:null
					},
					history:[]
				}
			}
		}
	},

	/**
	 * Builds a template intervals object.
	 */
	buildIntervalsObject: function() {

		return {
			progress:null
		}
	}
}

export default ProgressProcessing;